import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { CreateMailboxInput, UpdateMailboxInput } from './inputs.generated';

/**
 * Schránka faktur: e-mail (IMAP), ze kterého se přijaté faktury načítají samy, a přílohy,
 * které z něj přišly (zapsané, ke kontrole, přeskočené).
 */

export interface InvoiceMailbox {
  id: string;
  companyId: string;
  userId: string;
  host: string;
  port: number;
  username: string;
  folder: string;
  mode: 'auto' | 'review';
  onlyInvoices: boolean;
  since: string;
  isActive: boolean;
  lastCheckAt: string | null;
  nextCheckAt: string;
  lastError: string | null;
  createdAt: string;
}

export type InboxStatus =
  | 'new'
  | 'processing'
  | 'committed'
  | 'review'
  | 'duplicate'
  | 'ignored'
  | 'failed';

export interface InboxItem {
  id: string;
  companyId: string;
  mailboxId: string | null;
  fromAddress: string;
  fromName: string;
  subject: string;
  receivedAt: string;
  fileName: string;
  fileKind: string;
  status: InboxStatus;
  reason: string;
  supplierName: string;
  externalNumber: string;
  totalAmount: string | null;
  currency: string;
  hasFile: boolean;
  document: { id: string; number: string } | null;
  createdAt: string;
}

export function useMailboxes(companyId: string | null) {
  return useQuery({
    queryKey: ['inbox-mailboxes', companyId],
    queryFn: async () =>
      (
        await api.get<{ mailboxes: InvoiceMailbox[] }>(
          `/api/inbox/mailboxes?companyId=${companyId}`,
        )
      ).mailboxes,
    enabled: !!companyId,
  });
}

export function useInboxItems(companyId: string | null, status: 'open' | 'done' | 'all') {
  return useQuery({
    queryKey: ['inbox-items', companyId, status],
    queryFn: () =>
      api.get<{ items: InboxItem[]; counts: Partial<Record<InboxStatus, number>> }>(
        `/api/inbox/items?companyId=${companyId}&status=${status}`,
      ),
    enabled: !!companyId,
    // čtení přílohy přes AI trvá – rozpracované se obnovují samy
    refetchInterval: (q) =>
      (q.state.data?.items ?? []).some((i) => i.status === 'new' || i.status === 'processing')
        ? 4000
        : false,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['inbox-mailboxes'] });
    qc.invalidateQueries({ queryKey: ['inbox-items'] });
    qc.invalidateQueries({ queryKey: ['documents'] });
  };
}

export function useSaveMailbox() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; patch: UpdateMailboxInput } | { input: CreateMailboxInput }) =>
      'id' in v
        ? api.patch<{ mailbox: InvoiceMailbox }>(`/api/inbox/mailboxes/${v.id}`, v.patch)
        : api.post<{ mailbox: InvoiceMailbox }>('/api/inbox/mailboxes', v.input),
    onSuccess: invalidate,
  });
}

export function useRemoveMailbox() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/inbox/mailboxes/${id}`),
    onSuccess: invalidate,
  });
}

export function useCheckMailbox() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ messages: number; items: number; error?: string; mailbox: InvoiceMailbox }>(
        `/api/inbox/mailboxes/${id}/check`,
        {},
      ),
    onSettled: invalidate,
  });
}

export function useIgnoreInboxItem() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.post<{ item: InboxItem }>(`/api/inbox/items/${id}/ignore`, {}),
    onSuccess: invalidate,
  });
}

export function useRetryInboxItem() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.post<{ item: InboxItem }>(`/api/inbox/items/${id}/retry`, {}),
    onSettled: invalidate,
  });
}

/** Nejčastější poštovní servery – předvyplní adresu a dá radu k heslu. */
export const MAIL_PRESETS: { match: RegExp; host: string; label: string; hint: string }[] = [
  {
    match: /@(seznam|email|post|spoluzaci)\.cz$/i,
    host: 'imap.seznam.cz',
    label: 'Seznam',
    hint: 'V Seznam e-mailu zapni v nastavení „Přístup přes IMAP“ a když máš dvoufázové ověření, vytvoř heslo pro aplikace.',
  },
  {
    match: /@(gmail|googlemail)\.com$/i,
    host: 'imap.gmail.com',
    label: 'Gmail',
    hint: 'Gmail chce heslo pro aplikace: Účet Google → Zabezpečení → Dvoufázové ověření → Hesla aplikací.',
  },
  {
    match: /@(outlook|hotmail|live)\.(com|cz)$/i,
    host: 'outlook.office365.com',
    label: 'Outlook',
    hint: 'Outlook.com a Microsoft 365 často IMAP s heslem nepovolují – pak použij přeposílání faktur do schránky na Seznamu nebo Gmailu.',
  },
  {
    match: /@(centrum|atlas|volny)\.cz$/i,
    host: 'imap.centrum.cz',
    label: 'Centrum',
    hint: 'Použij heslo k e-mailu; IMAP musí být v nastavení povolený.',
  },
];

export function presetFor(email: string) {
  return MAIL_PRESETS.find((p) => p.match.test(email.trim())) ?? null;
}
